import { useState, useEffect, useCallback } from 'react'
import SidebarNavigation from '../components/SidebarNavigation'
import WebhookInstructions from '../components/WebhookInstructions'
import McpInstructions from '../components/McpInstructions'
import WebhookEventsFeed from '../components/WebhookEventsFeed'
import { getWebhookSecret, setWebhookSecret, authedFetch } from '../api'
import './SettingsPage.css'

function WebhookSecretField() {
  const [secret, setSecret] = useState(getWebhookSecret)
  const [saved, setSaved] = useState(false)

  const handleSave = () => {
    setWebhookSecret(secret)
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  return (
    <div className="webhook-secret-field">
      <label htmlFor="webhook-secret" className="webhook-secret-label">Webhook Secret</label>
      <p className="settings-section-description" style={{ margin: '0 0 0.5rem' }}>
        The server authenticates requests using the WEBHOOK_SECRET environment variable.
        Enter the matching secret here so your browser can make authenticated API requests.
      </p>
      <div className="webhook-secret-input-row">
        <input
          id="webhook-secret"
          type="password"
          className="webhook-secret-input"
          value={secret}
          onChange={e => { setSecret(e.target.value); setSaved(false) }}
          placeholder="Enter webhook secret"
        />
        <button
          className="webhook-secret-save-btn"
          onClick={handleSave}
        >
          Save
        </button>
        {saved && <span className="webhook-secret-status webhook-secret-saved">Saved to browser</span>}
      </div>
    </div>
  )
}

function BugReportWebhookField() {
  const [url, setUrl] = useState('')
  const [loading, setLoading] = useState(true)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    fetch('/.netlify/functions/settings')
      .then(r => r.json())
      .then((settings: Record<string, string>) => {
        setUrl(settings.bug_report_webhook_url || '')
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  const handleSave = async () => {
    await authedFetch('/.netlify/functions/settings', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bug_report_webhook_url: url }),
    })
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  if (loading) return <div>Loading...</div>

  return (
    <div className="webhook-secret-field">
      <label htmlFor="bug-report-webhook-url" className="webhook-secret-label">Bug Report Webhook URL</label>
      <p className="settings-section-description" style={{ margin: '0 0 0.5rem' }}>
        Internal webhook URL used by the Stage 2 refine agent. When the refine script fails,
        the agent posts a bug report here in addition to storing it in the benchmark database.
      </p>
      <div className="webhook-secret-input-row">
        <input
          id="bug-report-webhook-url"
          type="url"
          className="webhook-secret-input"
          value={url}
          onChange={e => { setUrl(e.target.value); setSaved(false) }}
          placeholder="https://example.com/webhook/bug-reports"
        />
        <button className="webhook-secret-save-btn" onClick={handleSave}>Save</button>
        {saved && <span className="webhook-secret-status webhook-secret-saved">Saved</span>}
      </div>
    </div>
  )
}

interface BugReport {
  id: string
  failure_id: string
  failure_short_id: string | null
  failure_title: string | null
  problems: string
  patch: string
  webhook_status: string
  created_at: string
}

function BugReportsList() {
  const [reports, setReports] = useState<BugReport[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [resetting, setResetting] = useState(false)

  const loadReports = useCallback(() => {
    fetch('/.netlify/functions/bug-reports')
      .then(r => r.json())
      .then((data: BugReport[]) => { setReports(data); setLoading(false) })
      .catch(() => setLoading(false))
  }, [])

  useEffect(() => { loadReports() }, [loadReports])

  const handleCopyAll = async () => {
    const text = reports.map(r => {
      let entry = `[${new Date(r.created_at).toLocaleString()}] ${r.failure_short_id || r.failure_id}: ${r.failure_title || 'Untitled'}\n`
      entry += `Problems:\n${r.problems}\n`
      if (r.patch) entry += `Patch:\n${r.patch}\n`
      return entry
    }).join('\n---\n\n')
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleReset = async () => {
    if (!confirm('Delete all bug reports? This cannot be undone.')) return
    setResetting(true)
    await authedFetch('/.netlify/functions/bug-reports', { method: 'DELETE' })
    setReports([])
    setResetting(false)
  }

  if (loading) return <div>Loading bug reports...</div>

  return (
    <div>
      <div className="bug-reports-actions" data-testid="bug-reports-actions">
        <button
          className="webhook-secret-save-btn"
          data-testid="bug-reports-copy-all"
          onClick={handleCopyAll}
          disabled={reports.length === 0}
        >
          {copied ? 'Copied!' : 'Copy All'}
        </button>
        <button
          className="webhook-secret-save-btn bug-reports-reset-btn"
          data-testid="bug-reports-reset"
          onClick={handleReset}
          disabled={reports.length === 0 || resetting}
        >
          {resetting ? 'Resetting...' : 'Reset'}
        </button>
      </div>
      {reports.length === 0 ? (
        <div className="settings-section-description">No bug reports filed yet.</div>
      ) : (
      <div className="clean-attempts-list">
      {reports.map(report => {
        const isExpanded = expandedId === report.id
        const ts = new Date(report.created_at).toLocaleString('en-US', {
          month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
        })
        return (
          <div key={report.id} className="clean-attempt-item">
            <button
              className="clean-attempt-toggle"
              onClick={() => setExpandedId(isExpanded ? null : report.id)}
              aria-expanded={isExpanded}
            >
              <span className="clean-events-toggle-icon" aria-hidden="true">{isExpanded ? '\u25BC' : '\u25B6'}</span>
              <span>{ts}</span>
              {report.failure_short_id && (
                <code className="clean-attempt-container">{report.failure_short_id}: {report.failure_title}</code>
              )}
              <span className={`event-type-badge ${report.webhook_status.startsWith('2') ? 'event-type-task' : 'event-type-error'}`}>
                {report.webhook_status || 'no webhook'}
              </span>
            </button>
            {isExpanded && (
              <div className="clean-attempt-events" style={{ padding: '0.75rem' }}>
                <div style={{ marginBottom: '0.75rem' }}>
                  <strong>Problems:</strong>
                  <pre className="failure-detail-pre" style={{ marginTop: '0.25rem' }}>{report.problems}</pre>
                </div>
                {report.patch && (
                  <div>
                    <strong>Suggested Patch:</strong>
                    <pre className="failure-detail-pre" style={{ marginTop: '0.25rem' }}>{report.patch}</pre>
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
      )}
    </div>
  )
}

function SettingsPage() {
  return (
    <div data-testid="settings-page" className="settings-page">
      <SidebarNavigation />

      <main className="app-page-main settings-main">
        <header className="app-page-sticky-header">
          <div className="app-page-sticky-header-inner">
            <h1 className="app-page-title settings-title">Settings</h1>
          </div>
        </header>

        <section className="settings-section">
          <h2 className="settings-section-title">Authentication</h2>
          <WebhookSecretField />
        </section>

        <section className="settings-section">
          <h2 className="settings-section-title">Webhook API Documentation</h2>
          <p className="settings-section-description">
            Use these endpoints to interact with the app programmatically. All endpoints accept and return JSON.
          </p>
          <WebhookInstructions />
        </section>

        <section className="settings-section">
          <h2 className="settings-section-title">MCP Server Documentation</h2>
          <p className="settings-section-description">
            Connect AI tools and agents to this app via the Model Context Protocol (MCP). The server exposes tools for querying and managing failures, runs, agents, and logs.
          </p>
          <McpInstructions />
        </section>

        <section className="settings-section">
          <h2 className="settings-section-title">Stage 2 Refine Bug Report Webhook</h2>
          <BugReportWebhookField />
        </section>

        <section className="settings-section">
          <h2 className="settings-section-title">Bug Reports</h2>
          <p className="settings-section-description">
            Bug reports filed by agents when the Stage 2 refine script encounters problems it cannot fix.
            Each report includes the problems log and a suggested patch to improve the script.
          </p>
          <BugReportsList />
        </section>

        <section className="settings-section">
          <h2 className="settings-section-title">Webhook Event Log</h2>
          <p className="settings-section-description">
            Events received from containers via the webhook endpoint. Shows the last 100 events.
          </p>
          <WebhookEventsFeed />
        </section>
      </main>
    </div>
  )
}

export default SettingsPage
